import { Suspense } from 'react'
import { motion } from 'motion/react'
import VideoViews, { VideoViewsSkeleton } from '@/components/content/videos/VideoViews'
import Tooltip from '@/components/ui/Tooltip'
import Pill from '@/components/ui/Pill'
import AppIcon from '@/components/content/app/AppIcon'
import ScrollView from '@/components/ui/ScrollView'
import { cn, getBrandColors, isPopulatedObject, timeToSeconds } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { getThumbnail } from '@/components/content/videos/helpers'
import VideoThumbnailImage from '@/components/content/videos/VideoThumbnailImage'
import YoutubePreconnect from '@/components/content/videos/YoutubePreconnect'
import { Icon } from '@/icons/Icon'
import type { App, Video, VideoAppsApp } from '@/payload-types'

type VideoThumbnailProps = {
  video: Video
  apps: VideoAppsApp[]
  isSelected?: boolean
  selectVideo: (autoPlay?: boolean, start?: string) => void
  setProjectedVideo: (value: VideoAppsApp | null) => void
}

export default function VideoThumbnail({
  video,
  apps,
  isSelected,
  selectVideo,
  setProjectedVideo,
}: VideoThumbnailProps) {
  const { duration, name } = video
  const thumbnail = getThumbnail(video)

  return (
    <div
      className={cn('relative flex items-center gap-4 py-2 pl-2 pr-4', isSelected ? 'group' : '')}
    >
      {thumbnail?.source === 'youtube' && <YoutubePreconnect />}
      <button
        type="button"
        onClick={() => selectVideo(false)}
        className="absolute inset-0 rounded-xl"
        aria-label={`Select ${name}`}
      />

      <div className="pointer-events-none relative z-10 aspect-video min-w-32 overflow-hidden rounded-md">
        {thumbnail ? (
          <VideoThumbnailImage
            key={thumbnail.url}
            thumbnail={thumbnail}
            quality="small"
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            alt={name ? name : 'Efficient App'}
            sizes={'(min-width: 768px) 50vw, 100vw'}
            fill
          />
        ) : (
          <div className="absolute h-full w-full bg-black"></div>
        )}

        <span className="absolute bottom-1.5 right-1.5 rounded-key bg-black/75 px-1 py-0.5 text-10 font-medium leading-none text-white">
          {duration}
        </span>
      </div>

      <div className="pointer-events-none relative z-10 flex min-w-0 flex-1 flex-col gap-0.5">
        <Tooltip
          text={video.name}
          delay={true}
          wrap={true}
          className="pointer-events-auto cursor-pointer"
        >
          <p
            className="truncate pl-1 text-left font-semibold text-gray-950"
            onClick={() => selectVideo(false)}
          >
            {video.name}
          </p>
        </Tooltip>

        {apps && apps.length > 0 && (
          <ScrollView className="flex items-center p-1" enhance={true}>
            {[...apps]
              .sort(
                (a, b) =>
                  timeToSeconds(a.timestampStart ?? '0:00') -
                  timeToSeconds(b.timestampStart ?? '0:00'),
              )
              .map((va) => {
                const app = va.app
                if (!isPopulatedObject<App>(app)) return null

                const { appIcon, appGlyph } = checkAppBranding(app)
                const { backgroundColor } = getBrandColors(app)

                return (
                  // Key on the outer element returned by .map (was on <AppIcon>
                  // which doesn't satisfy React's list-key requirement). `va.id`
                  // is the VideoAppsApp junction row id — unique per (video, app)
                  // pair.
                  <div
                    key={va.id}
                    onClick={() =>
                      isSelected
                        ? selectVideo(true, va.timestampStart)
                        : selectVideo(false, va.timestampStart)
                    }
                    onMouseEnter={() => (isSelected ? setProjectedVideo(va) : null)}
                    onMouseLeave={() => (isSelected ? setProjectedVideo(null) : null)}
                    aria-label={`Watch ${app.name} Segment`}
                    title={`Watch ${app.name} Segment`}
                    className="squircle pointer-events-auto relative mr-1 shrink-0 rotate-0 cursor-pointer overflow-hidden shadow-sm transition-all last:mr-3.5 sm:-mr-2 sm:-rotate-[8deg] sm:group-hover:mr-1 sm:group-hover:rotate-0"
                  >
                    <AppIcon
                      icon={appIcon}
                      glyph={appGlyph}
                      name={app.name}
                      background={backgroundColor}
                      size={24}
                    />

                    <span
                      className={cn(
                        'absolute inset-0 z-20 flex h-full w-full items-center justify-center bg-black/50 text-white opacity-0 transition-opacity',
                        isSelected ? 'hover:opacity-100' : '',
                      )}
                    >
                      <Icon name="media-play" size={12} />
                    </span>
                  </div>
                )
              })}
          </ScrollView>
        )}
      </div>

      <div className="flex flex-col items-center gap-1">
        <Pill
          type="button"
          style="outlinePrimary"
          className="pointer-events-auto relative z-10 ml-auto hidden md:flex"
          onClick={() => selectVideo(true)}
        >
          Watch
        </Pill>

        {video.youtubeId && (
          <Suspense fallback={<VideoViewsSkeleton />}>
            <VideoViews youtubeId={video.youtubeId} />
          </Suspense>
        )}
      </div>

      {isSelected && (
        <motion.div
          layoutId="selectedVideo"
          className="pointer-events-none absolute inset-0 z-0 rounded-xl border border-gray-200 bg-gray-50"
        ></motion.div>
      )}
    </div>
  )
}
